import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { startCase } from "lodash";
import { SharedAccordion } from "@/components/common/shared-accordion";
import { useGetTeamRolesQueryInfiniteQuery } from "@/services/(company)/api.teams";
import type { IPerformanceTeamMemberFilters } from "@/types/(company)/performance.interface";

interface Props {
  filters: IPerformanceTeamMemberFilters;
  onChange: (newFilters: IPerformanceTeamMemberFilters) => void;
}

const PerformanceTeamMemberFilters = ({ filters, onChange }: Props) => {
  const { t } = useTranslation(["common", "company", "my-space"]);
  const { data, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGetTeamRolesQueryInfiniteQuery({ limit: 20 });

  const roles = data?.pages.flatMap((page) => page.data) ?? [];

  const handleClear = (
    key: keyof IPerformanceTeamMemberFilters,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    onChange({ ...filters, [key]: "" });
  };

  return (
    <div>
      <SharedAccordion
        type="multiple"
        expanded
        items={[
          {
            label: t("company:performance.role_label"),
            content: (
              <FilterSelect
                label={t("company:performance.role_label")}
                placeholder={t("company:employees.select_role")}
                value={filters.roleId}
                onValueChange={(val) => onChange({ ...filters, roleId: val })}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                onLoadMore={fetchNextPage}
                isLoading={isFetching && !data}
              >
                <SelectItem value="all">{t("my-space:ui.all")}</SelectItem>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={String(role.id)}>
                    {startCase(role.roleName)}
                  </SelectItem>
                ))}
              </FilterSelect>
            ),
            suffix: <ClearButton onClick={(e) => handleClear("roleId", e)} />,
          },
        ]}
      />
    </div>
  );
};

export default PerformanceTeamMemberFilters;

// --- Sub-components ---

const ClearButton = ({
  onClick,
}: {
  onClick: (e: React.MouseEvent) => void;
}) => {
  const { t } = useTranslation(["common"]);
  return (
    <Button
      variant="link"
      size="sm"
      className="text-sm text-red-500 font-normal underline"
      onClick={onClick}
    >
      {t("common:buttons.clear")}
    </Button>
  );
};

interface FilterSelectProps {
  placeholder: string;
  label: string;
  value?: string;
  onValueChange: (val: string) => void;
  onOpenChange?: () => void;
  children: React.ReactNode;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  isLoading?: boolean;
}

const FilterSelect = ({
  placeholder,
  label,
  value,
  onValueChange,
  onOpenChange,
  children,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  isLoading,
}: FilterSelectProps) => {
  const { t } = useTranslation(["common", "company"]);
  return (
    <Select
      value={value}
      onValueChange={onValueChange}
      onOpenChange={onOpenChange}
    >
      <SelectTrigger className="h-min w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup onPointerDown={(e) => e.stopPropagation()}>
          <SelectLabel>{label}</SelectLabel>
          {isLoading ? (
            <SelectItem value="loading" disabled>
              {t("common:shared_ui.loading")}
            </SelectItem>
          ) : (
            <>
              {children}
              {hasNextPage && (
                <div className="p-1 border-t mt-1">
                  <button
                    className="text-xs text-primary font-medium"
                    disabled={isFetchingNextPage}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onLoadMore?.();
                    }}
                  >
                    {isFetchingNextPage
                      ? t("common:shared_ui.loading")
                      : t("company:performance.load_more")}
                  </button>
                </div>
              )}
            </>
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};
